import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SectionHeader from '../OpenDoor/SectionHeader';
import { REFINE_CHIPS } from './constants';

const formatVersionTime = (date) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date instanceof Date ? date : new Date(date));
  } catch {
    return '';
  }
};

const HandleReplySuggestedReplySection = ({
  hasGenerated,
  isMockMode,
  versions,
  activeVersionId,
  onSelectVersion,
  onRewrite,
  isRewriting,
}) => {
  const [refineText, setRefineText] = useState('');
  const activeVersion = versions.find((version) => version.id === activeVersionId) || versions[0];
  const showReplyUi = hasGenerated && Boolean(activeVersion);

  const handleCopy = async () => {
    if (!activeVersion?.body) return;
    try {
      await navigator.clipboard.writeText(activeVersion.body);
    } catch {
      // ignore clipboard errors in unsupported environments
    }
  };

  return (
    <Box
      border={1}
      borderColor='divider'
      borderRadius={2}
      p={2.5}
      display='flex'
      flexDirection='column'
      minHeight={{ md: 640 }}
      sx={{ opacity: showReplyUi ? 1 : 0.85 }}
    >
      <SectionHeader
        number={2}
        title='Review your suggested reply'
        endAdornment={
          showReplyUi ? (
            <Chip
              size='small'
              label={isMockMode ? 'UI mock — not AI' : 'AI generated'}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: isMockMode ? '#fff3e0' : '#e3f2fd',
                color: isMockMode ? '#e65100' : 'primary.main',
              }}
            />
          ) : null
        }
      />

      {!showReplyUi ? (
        <Box
          flex={1}
          border={1}
          borderColor='divider'
          borderRadius={1.5}
          bgcolor='#fff'
          p={2}
          display='flex'
          alignItems='center'
          justifyContent='center'
          minHeight={280}
        >
          <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic', textAlign: 'center' }}>
            Under construction.
            <br />
            Suggested replies will appear here after AI generation is connected.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            display='grid'
            gridTemplateColumns={{ xs: '1fr', sm: '1.4fr 1fr' }}
            gap={1.5}
            flex={1}
            mb={2}
          >
            <Box
              border={1}
              borderColor='divider'
              borderRadius={1.5}
              bgcolor='#fff'
              p={2}
              display='flex'
              flexDirection='column'
              minHeight={180}
            >
              <Box display='flex' alignItems='center' justifyContent='space-between' mb={1.5} gap={1}>
                <Box display='flex' alignItems='center' gap={0.25}>
                  <Typography variant='body2' fontWeight={700}>
                    Suggested Reply
                  </Typography>
                  {activeVersion?.whyThisReply ? (
                    <Tooltip title={activeVersion.whyThisReply} arrow placement='top'>
                      <Button
                        size='small'
                        startIcon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0, px: 0.75 }}
                      >
                        Why this reply?
                      </Button>
                    </Tooltip>
                  ) : null}
                </Box>
                <Button
                  size='small'
                  onClick={handleCopy}
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0 }}
                >
                  Copy
                </Button>
              </Box>
              <Typography
                variant='body2'
                color='text.primary'
                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, flex: 1 }}
              >
                {activeVersion.body}
              </Typography>
            </Box>

            <Box border={1} borderColor='divider' borderRadius={1.5} bgcolor='#fff' p={2} minHeight={180}>
              <Typography variant='body2' fontWeight={700} mb={1.5}>
                Version history
              </Typography>
              <Box display='flex' flexDirection='column' gap={0.75}>
                {versions.map((version) => {
                  const selected = version.id === activeVersionId;
                  return (
                    <Box
                      key={version.id}
                      component='button'
                      type='button'
                      onClick={() => onSelectVersion(version.id)}
                      sx={{
                        textAlign: 'left',
                        border: 1,
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? '#e3f2fd' : '#fff',
                        borderRadius: 1,
                        px: 1.25,
                        py: 1,
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant='body2' fontWeight={600}>
                        {version.label}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {formatVersionTime(version.createdAt)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box mt='auto' bgcolor='#fff' p={2} borderRadius={1.5} border={1} borderColor='divider'>
            <Typography variant='body2' fontWeight={700} mb={1}>
              Refine this reply
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={refineText}
              onChange={(event) => setRefineText(event.target.value)}
              placeholder="Tell me what you'd like me to improve…"
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 1.5 },
              }}
            />
            <Box display='flex' flexWrap='wrap' gap={0.75} mb={1.5}>
              {REFINE_CHIPS.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  size='small'
                  variant='outlined'
                  clickable
                  onClick={() => setRefineText(chip)}
                  sx={{
                    borderRadius: 5,
                    fontWeight: 500,
                    bgcolor: refineText === chip ? '#e3f2fd' : undefined,
                    borderColor: refineText === chip ? 'primary.main' : 'divider',
                    color: refineText === chip ? 'primary.main' : 'text.primary',
                    '&:hover': {
                      bgcolor: '#e3f2fd',
                      borderColor: 'primary.main',
                    },
                  }}
                />
              ))}
            </Box>
            <Box display='flex' justifyContent='flex-end'>
              <Button
                variant='contained'
                color='secondary'
                disabled={!refineText.trim() || isRewriting}
                onClick={() => {
                  onRewrite(refineText.trim());
                  setRefineText('');
                }}
                startIcon={<AutoAwesomeIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  bgcolor: '#7b1fa2',
                  '&:hover': { bgcolor: '#6a1b9a' },
                }}
              >
                Rewrite
              </Button>
            </Box>
            <Box display='flex' alignItems='flex-start' gap={0.75} mt={1.5}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.15 }} />
              <Typography variant='caption' color='text.secondary'>
                Tip: Each rewrite creates a new version. All versions are saved in this session.
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default HandleReplySuggestedReplySection;
